import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import {
  Calendar,
  FileText,
  Paperclip,
  Phone,
  Tag,
  User,
  Users,
  X,
} from "lucide-react";
import { useEtatGlobal } from "../EtatGlobal.js";

// Une ligne label/valeur dans une carte de détail — évite de répéter la même
// mise en page pour chaque champ de l'overlay.
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-xs py-1">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900 text-right">
      {value || "-"}
    </span>
  </div>
);

// Une carte de détail : icône + titre + contenu — même langage visuel que
// FicheClientOverlay/OverlayDetails.
const DetailCard = ({ icon: Icon, title, children, tone = "pink" }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-2">
      <div
        className={`w-7 h-7 rounded flex items-center justify-center ${
          tone === "pink" ? "bg-pink-100" : "bg-slate-100"
        }`}
      >
        <Icon
          className={`w-3.5 h-3.5 ${
            tone === "pink" ? "text-pink-600" : "text-slate-600"
          }`}
        />
      </div>
      <h3 className="font-semibold text-xs text-slate-900 m-0">{title}</h3>
    </div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const AllEvenement = () => {
  const { evenements, loadingEvent, users } = useEtatGlobal();
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Recherche & Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const [filterRows, setFilterRows] = useState([
    { column: "categorie", selectedValues: [] },
  ]);
  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);

  // Gestion fermeture overlay filtre clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        setShowFilterOverlay(false);
      }
    };

    if (showFilterOverlay)
      document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterOverlay]);

  // Valeurs uniques pour filtres
  const uniqueValues = useMemo(() => {
    const dict = {
      categorie: [],
      user: [],
    };

    evenements.forEach((evt) => {
      if (evt.categorie) dict.categorie.push(evt.categorie);
      if (evt.users && evt.users.length > 0)
        evt.users.forEach((u) =>
          dict.user.push(`${u.name || ""} ${u.lastName || ""}`.trim())
        );
    });

    Object.keys(dict).forEach((key) => {
      dict[key] = [...new Set(dict[key])];
    });
    return dict;
  }, [evenements]);

  // Application filtres + recherche
  const filteredEvenements = useMemo(() => {
    return evenements
      .filter((evt) => {
        const nomDossier = `${evt.formLead?.nom || ""} ${
          evt.formLead?.prenom || ""
        } ${evt.client?.nom || ""}`.toLowerCase();

        const matchSearch = nomDossier.includes(searchTerm);

        const matchFilters = filterRows.every((filter) => {
          if (!filter.selectedValues || filter.selectedValues.length === 0)
            return true;

          let val = "";
          if (filter.column === "categorie") val = evt.categorie;
          if (filter.column === "client") val = evt.client?.nom;
          if (filter.column === "user")
            val = evt.users?.map((u) => `${u.name} ${u.lastName}`).join(", ");

          return filter.selectedValues.some((f) => val?.includes(f));
        });

        return matchSearch && matchFilters;
      })
      // Plus récent en premier : date de création d'abord, date d'échéance
      // en départage pour deux événements créés le même jour.
      .sort((a, b) => {
        const createdDiff = new Date(b.createdAt) - new Date(a.createdAt);
        if (createdDiff !== 0) return createdDiff;
        return new Date(b.dateEcheance) - new Date(a.dateEcheance);
      });
  }, [evenements, searchTerm, filterRows]);

  const handleEventClick = (evt) => {
    setSelectedEvent(evt);
    setShowOverlay(true);
  };

  return (
    <div>
      <div className="Portefeuille-container">
          <p className="text-xs text-gray-400 mb-1">Acceuil &gt; Evènements</p>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Liste des Événements</h2>

          {/* Zone recherche & filtres */}
          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="relative flex-1 max-w-sm">
              <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom de dossier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
              />
            </div>
            <button
              onClick={() => setShowFilterOverlay(!showFilterOverlay)}
              className="flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
            >
              <CiFilter className="w-4 h-4" /> Filtrer
            </button>
          </div>

          {/* Overlay filtres */}
          {showFilterOverlay && (
            <div
              ref={overlayRef}
              className="fixed top-28 left-3/4 -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex justify-between mb-2">
                <p className="font-semibold">Filtres avancés</p>
                <button
                  className="text-sm text-slate-600 hover:text-slate-800"
                  onClick={() =>
                    setFilterRows([{ column: "categorie", selectedValues: [] }])
                  }
                >
                  Réinitialiser
                </button>
              </div>

              {filterRows.map((f, idx) => (
                <div key={idx} className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <select
                      value={f.column}
                      onChange={(e) =>
                        setFilterRows((prev) =>
                          prev.map((row, i) =>
                            i === idx
                              ? { ...row, column: e.target.value, selectedValues: [] }
                              : row
                          )
                        )
                      }
                      className="border border-slate-200 rounded-md p-1 text-sm"
                    >
                      <option value="categorie">Catégorie</option>
                      <option value="user">Utilisateur</option>
                    </select>
                    <button
                      className="text-sm text-red-600 hover:text-red-800"
                      onClick={() =>
                        setFilterRows((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      - Supprimer
                    </button>
                  </div>
                  <div className="flex flex-col ml-2">
                    {uniqueValues[f.column]?.map((val) => (
                      <label key={val}>
                        <input
                          type="checkbox"
                          checked={f.selectedValues.includes(val)}
                          onChange={() => {
                            const updated = f.selectedValues.includes(val)
                              ? f.selectedValues.filter((v) => v !== val)
                              : [...f.selectedValues, val];
                            setFilterRows((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? { ...row, selectedValues: updated }
                                  : row
                              )
                            );
                          }}
                        />
                        {val}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <button
                className="text-sm text-slate-700 hover:text-slate-900 mt-2"
                onClick={() =>
                  setFilterRows([
                    ...filterRows,
                    { column: "categorie", selectedValues: [] },
                  ])
                }
              >
                + Ajouter filtre
              </button>
            </div>
          )}

          {loadingEvent ? (
            <div className="mt-2 w-full overflow-x-auto animate-pulse">
              {/* En-tête simulé */}
              <div className="flex gap-2 mb-3 px-1">
                {[120, 80, 100, 90, 110, 70, 95].map((w, i) => (
                  <div
                    key={i}
                    className="h-4 bg-[#f0d6e0] rounded"
                    style={{ width: w }}
                  />
                ))}
              </div>
              {/* Lignes simulées */}
              {Array.from({ length: 12 }).map((_, rowIdx) => (
                <div
                  key={rowIdx}
                  className="flex gap-2 items-center px-1 py-2 border-b border-slate-100"
                >
                  {[120, 80, 100, 90, 110, 70, 95].map((w, i) => (
                    <div
                      key={i}
                      className="h-3 rounded"
                      style={{
                        width: w,
                        backgroundColor:
                          rowIdx % 2 === 0 ? "#f5e6ed" : "#f0f0f0",
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-2 w-full overflow-x-auto">
              <table
                className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
                style={{ fontSize: "0.68rem" }}
              >
                <thead>
                  <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Lead
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Client
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Tâches
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Catégorie
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Date d’échéance
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Descriptif
                    </th>
                    <th className="px-2 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                      Utilisateurs
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredEvenements
                    // Déjà trié (date de création puis échéance, plus récent
                    // en premier) par le useMemo ci-dessus.
                    .map((evt) => (
                      <tr
                        key={evt.id}
                        onClick={() => handleEventClick(evt)}
                        className="hover:bg-slate-50 transition-colors border-b border-slate-100 cursor-pointer"
                      >
                        <td className="px-2 py-2.5 font-bold text-slate-900">
                          {evt.formLead
                            ? `${evt.formLead.nom} ${evt.formLead.prenom}`
                            : ""}
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          {evt.client ? evt.client.nom : "-"}
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          <div className="tooltip-container">
                            {evt.note
                              ? evt.note.split(" ").slice(0, 3).join(" ") +
                                (evt.note.split(" ").length > 3 ? "..." : "")
                              : " "}
                            {evt.note && (
                              <div className="tooltip-text">{evt.note}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          {evt.categorie || "-"}
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          {new Date(evt.dateEcheance).toLocaleDateString()}
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          <div className="tooltip-container">
                            {evt.descriptif
                              ? evt.descriptif.split(" ").slice(0, 3).join(" ") +
                                (evt.descriptif.split(" ").length > 3
                                  ? "..."
                                  : "")
                              : " "}
                            {evt.descriptif && (
                              <div className="tooltip-text">
                                {evt.descriptif}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 font-medium">
                          {evt.users && evt.users.length > 0 ? (
                            <span
                              title={evt.users
                                .map((u) => `${u.name} ${u.lastName}`)
                                .join(", ")}
                            >
                              {evt.users
                                .map((u) => {
                                  const nomInitial =
                                    u.lastName?.[0]?.toUpperCase() || "";
                                  const prenomInitial =
                                    u.name?.[0]?.toUpperCase() || "";
                                  return `${prenomInitial}${nomInitial}`;
                                })
                                .join(" - ")}
                            </span>
                          ) : (
                            "Aucun"
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Overlay */}
      {showOverlay && selectedEvent && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayMS" ref={inoverlayRef}>
            <div className="relative px-6 pt-6 pb-2">
              <button
                onClick={() => setShowOverlay(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>

              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-[#9B1B5D]/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#9B1B5D]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 m-0">
                    Détails de l&apos;événement
                  </h3>
                  <p className="text-xs text-slate-400 m-0">
                    {new Date(selectedEvent.createdAt).toLocaleString("fr-FR")}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailCard icon={Tag} title="Informations générales">
                <InfoRow label="Catégorie" value={selectedEvent.categorie} />
                <InfoRow label="Descriptif" value={selectedEvent.descriptif} />
                <InfoRow label="Note" value={selectedEvent.note} />
              </DetailCard>

              <DetailCard icon={User} title="Lead & Client">
                <InfoRow
                  label="Lead"
                  value={
                    selectedEvent.formLead
                      ? `${selectedEvent.formLead.nom} ${selectedEvent.formLead.prenom}`
                      : "Aucun"
                  }
                />
                <InfoRow
                  label="Client"
                  value={selectedEvent.client ? selectedEvent.client.nom : "Aucun"}
                />
              </DetailCard>

              {/* Demande de rdv */}
              {selectedEvent.type === "Demande de rdv" && (
                <DetailCard
                  icon={Calendar}
                  title="Demande de rendez-vous"
                  tone="slate"
                >
                  <InfoRow
                    label="Date de rendez-vous"
                    value={
                      selectedEvent.dateRdv
                        ? new Date(selectedEvent.dateRdv).toLocaleDateString(
                            "fr-FR"
                          )
                        : null
                    }
                  />
                  <InfoRow
                    label="Heure de rendez-vous"
                    value={selectedEvent.heureRdv}
                  />
                  <InfoRow label="Site" value={selectedEvent.siteRdv} />
                  <InfoRow
                    label="Personne chez Alfa"
                    value={(() => {
                      const u = users?.find(
                        (x) => x.id === selectedEvent.personneAlfaId
                      );
                      return u ? `${u.name} ${u.lastName}` : null;
                    })()}
                  />
                </DetailCard>
              )}

              {/* Informations de contact s'ils existent */}
              {(selectedEvent.tel ||
                selectedEvent.email ||
                selectedEvent.sms ||
                selectedEvent.acceuil) && (
                <DetailCard icon={Phone} title="Contact">
                  {selectedEvent.tel && (
                    <InfoRow label="Téléphone" value={selectedEvent.tel} />
                  )}
                  {selectedEvent.email && (
                    <InfoRow label="Email" value={selectedEvent.email} />
                  )}
                  {selectedEvent.sms && (
                    <InfoRow label="SMS" value={selectedEvent.sms} />
                  )}
                  {selectedEvent.acceuil && (
                    <InfoRow label="Accueil" value={selectedEvent.acceuil} />
                  )}
                </DetailCard>
              )}

              {/* Utilisateurs */}
              <DetailCard icon={Users} title="Utilisateurs assignés">
                {selectedEvent.users && selectedEvent.users.length > 0 ? (
                  <ul className="list-none m-0 p-0 space-y-1">
                    {selectedEvent.users.map((user) => (
                      <li
                        key={user.id}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="font-medium text-slate-900">
                          {user.name} {user.lastName}
                        </span>
                        <span className="text-slate-400">{user.poste}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-400 m-0">Aucun utilisateur</p>
                )}
              </DetailCard>

              {/* Documents disponibles */}
              <DetailCard icon={Paperclip} title="Documents">
                {(() => {
                  const documents = [
                    { label: "Bilan", url: selectedEvent.bilanDocument },
                    {
                      label: "Pièce d'identité",
                      url: selectedEvent.identityDocument,
                    },
                    { label: "KBIS", url: selectedEvent.kbisDocument },
                    { label: "Statuts", url: selectedEvent.statusDocument },
                    {
                      label: "Pièce d'identité",
                      url: selectedEvent.pieceIdentite,
                    },
                  ].filter((doc) => doc.url);

                  if (documents.length === 0) {
                    return (
                      <p className="text-xs text-slate-400 m-0">
                        Aucun document
                      </p>
                    );
                  }

                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {documents.map((doc, i) => (
                        <a
                          key={i}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-pink-50 text-[#9B1B5D] hover:bg-pink-100 transition-colors"
                        >
                          <FileText size={12} />
                          {doc.label}
                        </a>
                      ))}
                    </div>
                  );
                })()}
              </DetailCard>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEvenement;
